import React, { useCallback, useEffect, useState } from 'react';
import type { GuardSiteDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { Empty, ErrorBanner, Panel, Pill } from '../ui';
import { CodeModal, NewSiteModal } from './SecurityPage';
import { SentinelChecks } from './SentinelChecks';
import { downloadSentinelSetup } from './DownloadsPage';
import { ask } from '../dialogs';

/**
 * Licenze Sentinel (29/10/2026, «una sola licenza»): una licenza per azienda, la stessa per controllo continuo (Guard),
 * verifica dei PC (Assessment), SIEM e Sonda. È quella di Monitoraggio: qui solo l'elenco, si crea con
 * «Proteggi un'azienda» e si cambia nelle Impostazioni dell'azienda.
 * Prima questa pagina mostrava le vecchie licenze autonome di Assessment (codici TA-…): spente, il programma non le usa più.
 */

const day = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
const pcs = (n: number) => (n === 1 ? '1 PC' : `${n} PC`);

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysTo(iso: string): number {
  return Math.round((Date.parse(`${iso}T00:00:00`) - Date.parse(`${today()}T00:00:00`)) / 86_400_000);
}

export function licenseState(s: GuardSiteDto): { label: string; tone: 'good' | 'warn' | 'bad' | 'soft'; tip: string } {
  const exp = s.licenseExpiresOn;
  if (s.licenseKind === 'trial') {
    if (!s.trialStartedAt) return { label: `Prova di ${s.trialDays ?? 14} giorni`, tone: 'soft', tip: 'I giorni partono quando si collega il primo PC.' };
    if (exp && daysTo(exp) < 0) return { label: 'Prova finita', tone: 'bad', tip: `La prova è finita il ${day(exp)}: i PC non mandano più dati.` };
    return { label: exp ? `Prova fino al ${day(exp)}` : 'In prova', tone: 'warn', tip: 'Per continuare, nelle Impostazioni passa a «pagata» con la nuova scadenza.' };
  }
  if (!exp) return { label: 'Senza scadenza', tone: 'good', tip: 'Licenza pagata senza data di fine.' };
  const left = daysTo(exp);
  if (left < 0) return { label: 'Scaduta', tone: 'bad', tip: `Scaduta il ${day(exp)}: i PC non mandano più dati.` };
  if (left <= 30) return { label: `Scade il ${day(exp)}`, tone: 'warn', tip: `Mancano ${left} giorni: va rinnovata.` };
  return { label: `Fino al ${day(exp)}`, tone: 'good', tip: 'Licenza pagata e valida.' };
}

const MODULES: { key: 'guard' | 'assessment' | 'siem' | 'sonda'; label: string }[] = [
  { key: 'guard', label: 'Monitoraggio continuo' },
  { key: 'assessment', label: 'Verifica PC' },
  { key: 'siem', label: 'SIEM' },
  { key: 'sonda', label: 'Sonda' },
];


export function Nis2LicensesPage({ ctx }: { ctx: Ctx }) {
  const [list, setList] = useState<GuardSiteDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState<{ site: string; code: string } | null>(null);
  const [openSite, setOpenSite] = useState<GuardSiteDto | null>(null);
  const [updates, setUpdates] = useState<{ latest: string | null; confirmed: string | null } | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await ctx.api.get<{ sites: GuardSiteDto[] }>('/api/guard/sites');
      setList(r.sites);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [ctx.api]);
  useEffect(() => {
    void load();
    ctx.api.get<{ latest: string | null; confirmed: string | null }>('/api/guard/updates').then(setUpdates).catch(() => undefined);
  }, [load, ctx.api]);

  const confirmVersion = async () => {
    if (!updates?.latest || !(await ask(`Confermare la versione ${updates.latest}? La riceveranno anche le aziende con «Solo versioni provate».`))) return;
    try {
      setUpdates(await ctx.api.post<{ latest: string | null; confirmed: string | null }>('/api/guard/updates/confirm'));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const devices = (list ?? []).reduce((n, s) => n + s.devices, 0);
  if (openSite) return <SentinelChecks ctx={ctx} site={openSite} onBack={() => setOpenSite(null)} />;
  return (
    <div className="panels single">
      <Panel
        title="Licenze Sentinel"
        subtitle={list ? `${list.length === 1 ? '1 azienda' : `${list.length} aziende`} · ${pcs(devices)} collegati` : undefined}
        right={
          <div className="row-actions" style={{ margin: 0 }}>
            <button type="button" className="btn" title="Il file da portare sui PC dei clienti" onClick={() => void downloadSentinelSetup(ctx).catch((e) => setError((e as Error).message))}>
              <Icon name="download" size={16} /> Scarica Setup Sentinel.exe
            </button>
            <button type="button" className="btn dark" title="Crea la licenza e il codice da dare al cliente" onClick={() => setCreating(true)}>
              <Icon name="plus" size={16} /> Proteggi un’azienda
            </button>
          </div>
        }
      >
        <div className="muted small" style={{ marginBottom: 12 }}>
          Una sola licenza per azienda: vale per tutto Sentinel (monitoraggio continuo, verifica dei PC, SIEM e sonda). Clicca un’azienda per vedere le verifiche eseguite. Tipo, scadenza, numero di PC e moduli si cambiano
          nelle Impostazioni dell’azienda in Monitoraggio.
        </div>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {updates?.latest && (
          <div className="ag-row" style={{ marginBottom: 12 }}>
            <Icon name="refresh" size={18} />
            <span className="ag-title">
              <b>Versione pubblicata {updates.latest}</b>
              <div className="muted small">Versione provata {updates.confirmed ?? 'nessuna'}: la ricevono le aziende con «Solo versioni provate». Si sceglie nelle Impostazioni di ogni azienda.</div>
            </span>
            {updates.latest !== updates.confirmed
              ? <button type="button" className="btn" title="La manda anche alle aziende prudenti" onClick={() => void confirmVersion()}>Conferma questa versione</button>
              : <Pill tone="good">Confermata</Pill>}
          </div>
        )}
        {!list ? (
          <div className="muted small">Caricamento…</div>
        ) : list.length === 0 ? (
          <Empty icon="key">Nessuna azienda protetta: premi «Proteggi un’azienda».</Empty>
        ) : (
          <div className="ag-list">
            {list.map((s) => {
              const st = licenseState(s);
              const on = MODULES.filter((m) => s.modules[m.key]).map((m) => m.label);
              return (
                <div key={s.id} className="ag-row" onClick={() => setOpenSite(s)} title="Vedi le verifiche eseguite da Sentinel e i risultati">
                  <Icon name="key" size={18} />
                  <span className="ag-title">
                    <b>{s.name}</b>
                    <div className="muted small">
                      {s.devices} di {s.deviceLimit ? pcs(s.deviceLimit) : 'PC senza limite'} · {s.online} in linea
                      {on.length ? ` · ${on.join(', ')}` : ' · nessun modulo attivo'}
                    </div>
                  </span>
                  <Pill tone={st.tone} title={st.tip}>{st.label}</Pill>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
      {creating && (
        <NewSiteModal
          ctx={ctx}
          onClose={() => setCreating(false)}
          onCreated={(site, c) => {
            setCreating(false);
            setCode({ site: site.name, code: c });
            void load();
          }}
        />
      )}
      {code && <CodeModal ctx={ctx} site={code.site} code={code.code} onClose={() => setCode(null)} />}
    </div>
  );
}
